// eslint-disable-next-line no-unused-vars
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import _find from 'lodash/find';
import _assign from 'lodash/assign';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _cloneDeep from 'lodash/cloneDeep';
import _camelCase from 'lodash/camelCase';
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment';

import schemaModule from '../modules/schema';
import { getLogger } from '../logger';
import { toBoolean } from './utils';

const logger = getLogger('use-meta');

function convertValue(propertyDescriptor, value) {
  if (
    propertyDescriptor.complexType &&
    !propertyDescriptor.array &&
    propertyDescriptor.complexMeta &&
    propertyDescriptor.complexMeta.data
  ) {
    return Object.keys(value || {}).reduce((convVal, key) => {
      if (Object.keys(propertyDescriptor.complexMeta.data).includes(key)) {
        return {
          ...convVal,
          [key]: convertValue(propertyDescriptor.complexMeta.data[key], value[key]),
        };
      }
      return convVal;
    }, {});
  }

  switch (propertyDescriptor.type) {
    case 'float':
      return value ? parseFloat(value) : null;
    case 'integer':
      return value ? parseInt(value, 10) : null;
    case 'string':
      return value ? value.toString() : value;
    case 'array':
      if (!Array.isArray(value)) {
        return [value];
      }
      return value;
    case 'boolean':
      return toBoolean(value);
    case 'DateTime':
      return moment(value)
        .utc()
        .toISOString(true);
    default:
      return value;
  }
}

function createPath(path, pathPart, target, descriptor, descriptors, filters, value) {
  const propertyDescriptor = descriptor[pathPart];
  let currentValue;
  if (target[pathPart]) {
    currentValue = target[pathPart];
  } else {
    if (propertyDescriptor.complexType && propertyDescriptor.array) {
      currentValue = [];
    } else if (propertyDescriptor.complexType) {
      currentValue = {};
    }

    if (currentValue) {
      // eslint-disable-next-line no-param-reassign
      target[pathPart] = currentValue;
    }
  }
  const newPathPart = path.shift();
  if (newPathPart) {
    if (
      !propertyDescriptor.complexMeta &&
      (!descriptors[propertyDescriptor.type] || !descriptors[propertyDescriptor.type].data)
    ) {
      return { fetchType: true, propertyDescriptor };
    }
    const subDescriptor = (propertyDescriptor.complexMeta || descriptors[propertyDescriptor.type]).data;
    if (propertyDescriptor.array) {
      let entry = _find(currentValue, filters);
      if (!entry) {
        const newEntry = {};
        Object.keys(subDescriptor).forEach(key => {
          newEntry[key] = filters[key] || null;
        });
        entry = newEntry;
        currentValue.push(entry);
      }
      currentValue = entry;
    }
    return createPath(path, newPathPart, currentValue, subDescriptor, descriptors, filters, value);
  }
  // HOTFIX
  if (propertyDescriptor.name === 'category_path') {
    console.log('Parameters', path, pathPart, target, descriptor, descriptors, filters, value);
  }
  // eslint-disable-next-line no-param-reassign
  target[pathPart] = convertValue(propertyDescriptor, value);

  return undefined;
}

function resolvePath(path, pathPart, source, descriptor, descriptors, filters) {
  const propertyDescriptor = descriptor[pathPart];
  let currentValue = source[_camelCase(propertyDescriptor.name)] || source[propertyDescriptor.name];

  if (currentValue && propertyDescriptor.complexType) {
    if (
      propertyDescriptor.complexMeta ||
      (descriptors[propertyDescriptor.type] && descriptors[propertyDescriptor.type].data)
    ) {
      const newPathPart = path.shift();
      if (!newPathPart) {
        return currentValue;
      }
      if (propertyDescriptor.array) {
        if (!filters) {
          logger.error('Try to access entity array data without filters. CurrentValue', currentValue);
          return undefined;
        }
        if (!Array.isArray(currentValue)) {
          logger.error('Expected array but got', typeof currentValue, 'Value', currentValue);
          return undefined;
        }
        currentValue = _find(currentValue, filters);
        if (!currentValue) {
          return undefined;
        }
      }
      const subDescriptor = propertyDescriptor.complexMeta || descriptors[propertyDescriptor.type];
      return resolvePath(path, newPathPart, currentValue, subDescriptor.data, descriptors, filters);
    }
    return { fetchType: true, propertyDescriptor };
  }
  if (currentValue) {
    switch (propertyDescriptor.type) {
      case 'boolean':
        return toBoolean(currentValue);
      // case 'integer':
      //          return parseInt(currentValue, 10);
      // case 'float':
      //          return parseFloat(currentValue);
      default:
        return currentValue.toString();
    }
  }
  return undefined;
}

function getter(source, propertyPath, descriptor, descriptors, filters) {
  if (!source || !descriptor) {
    return undefined;
  }
  const path = propertyPath.split('.');
  const pathPart = path.shift();
  const result = resolvePath(path, pathPart, source, descriptor, descriptors, filters);
  logger.trace('Get value Source', source, 'PropertyPath', propertyPath, 'Filters', filters, 'Result', result);
  return result;
}

function setter(target, propertyPath, descriptor, descriptors, filters, value) {
  const path = propertyPath.split('.');
  const pathPart = path.shift();
  const result = createPath(path, pathPart, target, descriptor, descriptors, filters, value);
  if (result && result.fetchType) {
    logger.trace('Not set value to Target', target, 'Result', result);
  } else {
    logger.debug('Set value to Target', target, 'PropertyPath', propertyPath, 'Filters', filters, 'Value', value);
  }
  return result;
}

function useMeta(source, metaId, filters) {
  const dispatch = useDispatch();
  const schemaDescriptors = useSelector(schemaModule.selectors.getSchemaDescriptors);
  const isLoading = useSelector(schemaModule.selectors.isPending);
  const [editableSource, setEditableSource] = useState(source);

  useEffect(() => {
    // Ensure meta data descriptor for the given metaId
    if ((!schemaDescriptors[metaId] || !schemaDescriptors[metaId].data) && !isLoading) {
      logger.trace('Fetch meta data for metaId:', metaId);
      dispatch(schemaModule.actions.loadMetadata({ metaId }));
    }
  }, [metaId, isLoading]);

  const getProperty = useCallback(
    (propertyPath, defaultValue) => {
      if (!schemaDescriptors[metaId] || !schemaDescriptors[metaId].data || isLoading) {
        return defaultValue;
      }
      const result = getter(editableSource, propertyPath, schemaDescriptors[metaId].data, schemaDescriptors, filters);
      // If the result tells us to fetch a missing meta data descriptor...
      if (
        result != null &&
        typeof result === 'object' &&
        result.fetchType &&
        (!schemaDescriptors[result.type] ||
          (!schemaDescriptors[result.type].pending && !schemaDescriptors[result.type].data))
      ) {
        logger.trace('Fetch meta data for metaId:', result);
        // ... -> fetch it
        dispatch(schemaModule.actions.loadMetadata({ metaId: result.propertyDescriptor.type }));
        return defaultValue;
      }
      if (result === undefined) {
        return defaultValue;
      }
      return result;
    },
    [editableSource, schemaDescriptors, filters]
  );
  const setProperty = useCallback(
    propertyPath => {
      if (!schemaDescriptors[metaId]) {
        return () => {};
      }
      const descriptor = schemaDescriptors[metaId].data;
      const descriptors = schemaDescriptors;
      return function setPropertyHandler(e, value, prioritySource) {
        const target = {};
        const result = setter(target, propertyPath, descriptor, descriptors, filters, value != null ? value : e);
        // If the result tells us to fetch a missing meta data descriptor...
        if (
          result != null &&
          result.fetchType &&
          (!descriptors[result.type] || (!descriptors[result.type].pending && !descriptors[result.type].data))
        ) {
          logger.trace('Fetch meta data for metaId:', result);
          // ... -> fetch it
          dispatch(schemaModule.actions.loadMetadata({ metaId: result.propertyDescriptor.type }));
        }
        if (result === undefined) {
          const newSource = _assign({}, prioritySource || editableSource, target);
          // We have a new instance of the source -> propagate it
          setEditableSource(newSource);
          return newSource;
        }
        return editableSource;
      };
    },
    [editableSource, schemaDescriptors, filters, isLoading]
  );

  const getTranslatableProperty = useCallback(
    (propertyPath, lang, transPropName) => {
      const tranlatableList = _get(editableSource, propertyPath);
      if (!tranlatableList) {
        return null;
      }
      const translationObject = _find(tranlatableList, { lang });
      if (!translationObject) {
        return null;
      }
      const parentPropName = propertyPath.split('.').pop();

      return translationObject[transPropName || parentPropName];
    },
    [editableSource]
  );

  const setTranslatableProperty = useCallback(
    (propertyPath, lang, transPropName) => {
      return function setPropertyHandler(e, value) {
        const newSource = _cloneDeep(source);
        let tranlatableList = _get(newSource, propertyPath);
        if (!tranlatableList) {
          tranlatableList = [];
          _set(newSource, propertyPath, tranlatableList);
        }

        let translationObject = _find(tranlatableList, { lang });
        if (!translationObject) {
          translationObject = {
            lang,
          };
          tranlatableList.push(translationObject);
        }
        const parentPropName = propertyPath.split('.').pop();

        translationObject[transPropName || parentPropName] = value || e;
        setEditableSource(newSource);
      };
    },
    [source]
  );

  return [
    editableSource,
    getProperty,
    setProperty,
    getTranslatableProperty,
    setTranslatableProperty,
    isLoading,
    schemaDescriptors,
  ];
}

export default useMeta;
